import { useState } from 'react';

import { EmojiClickData } from 'emoji-picker-react';
import { SmileIcon } from 'lucide-react';
import { ControllerRenderProps } from 'react-hook-form';

import { EmojiURL } from '@/components/atoms/EmojiURL/EmojiURL';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';

import { EmojiClick, EmojiPicker } from '../../../EmojiPicker/EmojiPicker';

interface EmojiInputProps {
  field: Omit<ControllerRenderProps, 'name' | 'value'> & { name: string; value?: string };
  label?: string;
  placeholder?: string;
  error?: boolean;
  errorMessage?: string;
  onEmojiClick: EmojiClick;
  emojiPlaceholder?: string;
  setEmojiMenuOpen: React.Dispatch<React.SetStateAction<boolean>>;
  isEmojiMenuOpen: boolean;
}
export const EmojiInput = ({
  field,
  label,
  placeholder,
  error,
  errorMessage,
  onEmojiClick,
  emojiPlaceholder,
  setEmojiMenuOpen,
  isEmojiMenuOpen,
}: EmojiInputProps) => {
  const [emoji, setEmoji] = useState<Partial<EmojiClickData> | null>(
    emojiPlaceholder ? { imageUrl: emojiPlaceholder, names: ['default icon'] } : null
  );
  const handleClickAway = () => {
    setEmojiMenuOpen(false);
  };

  const handleSetEmoji = (emoji: EmojiClickData, event: MouseEvent) => {
    onEmojiClick(emoji, event);
    setEmoji(emoji);
    setEmojiMenuOpen(false);
  };

  const EmojiField = () => {
    return (
      <div className="absolute top-1/2 right-2.5 -translate-y-1/2">
        <Button
          variant="ghost-neutral"
          size="icon-sm"
          aria-label="Smiley Face"
          onClick={() => setEmojiMenuOpen((p) => !p)}
        >
          {emoji ? (
            <EmojiURL url={emoji?.imageUrl} alt={emoji?.names[0]} />
          ) : (
            <SmileIcon size={18} />
          )}
        </Button>
        {isEmojiMenuOpen && (
          <EmojiPicker
            onEmojiClick={handleSetEmoji}
            width={256}
            height={300}
            onClickAway={handleClickAway}
          />
        )}
      </div>
    );
  };
  return (
    <div>
      {label && (
        <Label htmlFor={field.name} className={cn('mb-2 block', error && 'text-warning')}>
          {label}
        </Label>
      )}
      <div className="relative">
        <Input
          {...field}
          value={field.value ?? ''}
          id={field.name}
          placeholder={placeholder}
          aria-invalid={error}
        />
        <EmojiField />
      </div>
      {error && errorMessage && <p className="mt-1 text-sm text-warning">{errorMessage}</p>}
    </div>
  );
};
